import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import StructuredData from './StructuredData';
import { generateBreadcrumbSchema } from '../utils/seo';
import { Container } from './shared/SharedComponents';
import { useContent } from '../hooks/useContent';
import LocaleLink from './LocaleLink';
import { getPathWithoutLocale } from '../context/LocaleContext';

/** Build path -> display name map from content.json navigation, solutions, and footer links */
function buildPathToNameMap(site, solutions, footerInfo, staticPages, strategicIntelligence) {
  const map = {};
  const nav = site?.navigation || [];
  nav.forEach((item) => {
    if (item.path) map[item.path] = item.name;
    (item.items || []).forEach((sub) => {
      if (sub.path) map[sub.path] = sub.name;
      (sub.subItems || []).forEach((subSub) => {
        if (subSub.path) map[subSub.path] = subSub.name;
      });
    });
  });
  // Footer links (Legal Notice, Disclaimer, Privacy Policy, Terms & Conditions) - locale-aware
  (footerInfo?.links || []).forEach((link) => {
    if (link.path && link.label) map[link.path] = link.label;
  });
  // Static/legal page titles as fallback (disclaimer, privacyPolicy, termsAndConditions, legalNotice)
  if (staticPages?.disclaimer?.title) map['/disclaimer'] = map['/disclaimer'] || staticPages.disclaimer.title;
  if (staticPages?.privacyPolicy?.title) map['/privacy-policy'] = map['/privacy-policy'] || staticPages.privacyPolicy.title;
  if (staticPages?.termsAndConditions?.title) map['/terms-and-conditions'] = map['/terms-and-conditions'] || staticPages.termsAndConditions.title;
  if (staticPages?.legalNotice?.title) map['/legal-notice'] = map['/legal-notice'] || staticPages.legalNotice.title;
  // Fallback: solutions sections/subsections by path (in case nav is out of sync)
  const sections = solutions?.sections || {};
  Object.entries(sections).forEach(([sectionId, section]) => {
    const sectionPath = `/solutions/${sectionId}`;
    if (!map[sectionPath] && section?.title) map[sectionPath] = section.title;
    const subsections = section?.subsections || {};
    Object.entries(subsections).forEach(([subsectionId, subsection]) => {
      const subsectionPath = `/solutions/${sectionId}/${subsectionId}`;
      if (!map[subsectionPath] && subsection?.title) map[subsectionPath] = subsection.title;
    });
  });
  // Strategic Intelligence categories and articles
  if (strategicIntelligence?.title) map['/strategic-intelligence'] = strategicIntelligence.title;
  const categories = strategicIntelligence?.categories || {};
  Object.entries(categories).forEach(([categoryId, category]) => {
    const categoryPath = `/strategic-intelligence/${categoryId}`;
    if (category?.title) map[categoryPath] = category.title;
    const articlePages = category?.articlePages || {};
    Object.entries(articlePages).forEach(([articleId, article]) => {
      const articlePath = `/strategic-intelligence/${categoryId}/${articleId}`;
      if (article?.title) map[articlePath] = article.title;
    });
  });
  return map;
}

function fallbackNameFromSegment(segment) {
  return segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const BreadcrumbsContainer = styled.nav`
  padding: 24px 0 16px;
  
  @media (max-width: 768px) {
    padding: 20px 0 12px;
  }
`;

const BreadcrumbsList = styled.ol`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 0;
`;

const BreadcrumbItem = styled.li`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: rgba(29, 51, 78, 0.8);

  &:not(:last-child)::after {
    content: '/';
    margin: 0 12px;
    color: rgba(29, 51, 78, 0.4);
  }
`;

const BreadcrumbLink = styled(LocaleLink)`
  color: rgba(29, 51, 78, 0.8);
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #BF1922;
    text-decoration: underline;
  }

  &[aria-current="page"] {
    color: #1d334e;
    font-weight: 600;
    pointer-events: none;
  }
`;

const BreadcrumbSpan = styled.span`
  color: #1d334e;
  font-weight: 600;
`;

const HomeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden="true">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

/**
 * Breadcrumbs Component
 * Displays navigation breadcrumbs with structured data for SEO.
 * Uses content.json navigation and solutions for display names when available.
 */
const Breadcrumbs = ({ items = null }) => {
  const location = useLocation();
  const { site, solutions, footerInfo, disclaimer, privacyPolicy, termsAndConditions, legalNotice, content } = useContent();
  const strategicIntelligence = content?.pages?.strategicIntelligence;
  const pathToName = useMemo(
    () => buildPathToNameMap(site, solutions, footerInfo, { disclaimer, privacyPolicy, termsAndConditions, legalNotice }, strategicIntelligence),
    [site, solutions, footerInfo, disclaimer, privacyPolicy, termsAndConditions, legalNotice, strategicIntelligence]
  );

  // Generate breadcrumbs from route if not provided
  const generateBreadcrumbsFromRoute = () => {
    const pathWithoutLocale = getPathWithoutLocale(location.pathname);
    const pathSegments = pathWithoutLocale.split('/').filter(Boolean);
    const breadcrumbs = [{ name: 'Home', path: '/', url: '/' }];

    let currentPath = '';
    pathSegments.forEach((segment) => {
      currentPath += `/${segment}`;

      // Skip displaying "solutions" as its own crumb (no /solutions-only route)
      if (segment === 'solutions') {
        return;
      }

      const name = pathToName[currentPath] || fallbackNameFromSegment(segment);
      breadcrumbs.push({
        name,
        path: currentPath,
        url: currentPath
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = items || generateBreadcrumbsFromRoute();

  // Don't show breadcrumbs on home page
  const pathWithoutLocale = getPathWithoutLocale(location.pathname);
  if (pathWithoutLocale === '/' || pathWithoutLocale === '') {
    return null;
  }

  // Generate structured data for breadcrumbs
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);

  return (
    <>
      <BreadcrumbsContainer aria-label="Breadcrumb navigation">
        <Container>
          <BreadcrumbsList>
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;
              const isHome = crumb.path === '/' || crumb.url === '/';
              const linkContent = isHome ? <HomeIcon /> : crumb.name;
              return (
                <BreadcrumbItem key={crumb.path || index}>
                  {isLast ? (
                    <BreadcrumbSpan aria-current="page">
                      {linkContent}
                    </BreadcrumbSpan>
                  ) : (
                    <BreadcrumbLink to={crumb.path || crumb.url} aria-label={isHome ? crumb.name : undefined}>
                      {linkContent}
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              );
            })}
          </BreadcrumbsList>
        </Container>
      </BreadcrumbsContainer>
      {breadcrumbSchema && (
        <StructuredData
          type="WebPage"
          data={{ breadcrumbs }}
          breadcrumbs={breadcrumbs}
          includeOrganization={false}
          includeWebSite={false}
        />
      )}
    </>
  );
};

export default Breadcrumbs;
